/**
 * Participants d'un challenge — règles PURES, lues par le serveur (`src/api/challenges.ts`)
 * et par les écrans. S'appuie sur la règle partagée `canBeProposedAsParticipant`
 * (`participant-eligibility.ts`, décision 3.6 : le RH et le GES ne s'entraînent pas).
 */
import { canBeProposedAsParticipant } from "./participant-eligibility";

/** Code d'erreur renvoyé par le serveur (le message affiché est traduit par l'écran). */
export const PARTICIPANT_NOT_PROPOSABLE = "participant_not_proposable";
/** Code d'erreur : un RH / GES tente de s'inscrire lui-même à un challenge facultatif. */
export const JOIN_NOT_PROPOSABLE = "join_not_proposable";
/** Code d'erreur : inscription ou partie demandée dans un challenge en pause. */
export const CHALLENGE_PAUSED = "challenge_paused";
/** Codes d'erreur de la pause, de la reprise et de la clôture d'un challenge. */
export const CHALLENGE_STATE_CHANGED = "challenge_state_changed";
export const CHALLENGE_NOT_ACTIVE = "challenge_not_active";
export const CHALLENGE_NOT_PAUSED = "challenge_not_paused";
export const CHALLENGE_NOT_OPEN = "challenge_not_open";
/** Code d'erreur : reprise refusée, la date de fin est passée pendant la pause (30/09/2026). */
export const CHALLENGE_END_DATE_PASSED = "challenge_end_date_passed";
/** Codes d'erreur de la suppression et de l'activation d'un brouillon (audit du lot 10 : refus sans code). */
export const CHALLENGE_DELETE_NOT_DRAFT = "challenge_delete_not_draft";
export const CHALLENGE_DELETE_HAS_LIVED = "challenge_delete_has_lived";
export const CHALLENGE_ACTIVATE_NOT_DRAFT = "challenge_activate_not_draft";
export const CHALLENGE_AWAITS_CAMPAIGN_DATES = "challenge_awaits_campaign_dates";

/**
 * Personnes REFUSÉES dans une liste de participants proposés : les profils qui ne s'entraînent
 * pas (RH, GES)… sauf ceux qui y figurent DÉJÀ (inscrits ou pré-affectés) — une inscription
 * existante n'est jamais remise en cause (décision 1 du 30/09/2026).
 */
export function refusedParticipantIds(
  candidates: { id: string; profileType?: string | null }[],
  alreadyIn: Iterable<string> = [],
): string[] {
  const kept = new Set(alreadyIn);
  return candidates.filter((u) => !kept.has(u.id) && !canBeProposedAsParticipant(u.profileType)).map((u) => u.id);
}

/**
 * Brouillon : les RH / GES pré-affectés ou inscrits en brouillon sont RETIRÉS (décision de Valérie,
 * 30/09/2026, point 4) — au plus tard à l'activation, jamais inscrits. Seuls ceux déjà inscrits dans
 * un challenge EN COURS (ou en pause) gardent leur place. Rend les lignes à garder et les retirées.
 */
export function withoutNonTrainees<T extends { userId: string }>(
  rows: readonly T[],
  profileOf: (userId: string) => string | null | undefined,
): { kept: T[]; removed: T[] } {
  const kept: T[] = [];
  const removed: T[] = [];
  for (const r of rows) (canBeProposedAsParticipant(profileOf(r.userId)) ? kept : removed).push(r);
  return { kept, removed };
}

/**
 * Un challenge est-il PROPOSÉ à cette personne dans sa liste (bannière, page Challenges) ?
 * Décision de Valérie du 30/09/2026 : le RH et le GES ne se voient plus proposer les challenges
 * facultatifs ; un challenge où ils sont déjà inscrits reste visible (ils gardent leur accès).
 */
export function isChallengeProposedTo(
  profileType: string | null | undefined,
  challenge: { challengeType?: string | null },
  enrolled: boolean,
): boolean {
  if (enrolled) return true;
  if (challenge.challengeType !== "optional") return true;
  return canBeProposedAsParticipant(profileType);
}

/** Clés i18n des codes d'erreur des challenges. */
export const CHALLENGE_ERROR_KEYS: Record<string, string> = {
  [PARTICIPANT_NOT_PROPOSABLE]: "challenge.errors.participantNotProposable",
  [JOIN_NOT_PROPOSABLE]: "challenge.errors.joinNotProposable",
  [CHALLENGE_PAUSED]: "challenge.errors.paused",
  [CHALLENGE_STATE_CHANGED]: "challenge.errors.stateChanged",
  [CHALLENGE_NOT_ACTIVE]: "challenge.errors.notActive",
  [CHALLENGE_NOT_PAUSED]: "challenge.errors.notPaused",
  [CHALLENGE_NOT_OPEN]: "challenge.errors.notOpen",
  [CHALLENGE_END_DATE_PASSED]: "challenge.errors.endDatePassed",
  [CHALLENGE_DELETE_NOT_DRAFT]: "challenge.errors.deleteNotDraft",
  [CHALLENGE_DELETE_HAS_LIVED]: "challenge.errors.deleteHasLived",
  [CHALLENGE_ACTIVATE_NOT_DRAFT]: "challenge.errors.activateNotDraft",
  [CHALLENGE_AWAITS_CAMPAIGN_DATES]: "challenge.errors.awaitsCampaignDates",
};

/**
 * Message à afficher pour une erreur d'API challenge : traduit quand le serveur a donné un code
 * connu, sinon le message du serveur, sinon le message générique.
 */
export function challengeErrorMessage(e: unknown, t: (key: string) => string): string {
  const code = (e as { code?: string } | null)?.code;
  if (code && CHALLENGE_ERROR_KEYS[code]) return t(CHALLENGE_ERROR_KEYS[code]);
  if (e instanceof Error && e.message) return e.message;
  return t("common.error");
}
